import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import PageHeader from "@/components/app/PageHeader";
import { Link } from "react-router-dom";
import { AlertTriangle, AlertOctagon, Info } from "lucide-react";

const SEV_STYLE = {
  high: "bg-red-50 text-red-800 border-red-200",
  medium: "bg-amber-50 text-amber-800 border-amber-200",
  low: "bg-gray-100 text-gray-700 border-gray-300",
};
const SEV_ICON = {
  high: AlertOctagon,
  medium: AlertTriangle,
  low: Info,
};

const ENTITY_LINK = {
  hauler: (id) => `/haulers`,
  driver: (id) => `/drivers`,
  load: (id) => `/loads`,
  hauler_invoice: (id) => `/hauler-invoices/${id}`,
  stockpile: (id) => `/stockpiles`,
};

export default function Exceptions() {
  const [items, setItems] = useState([]);
  const [sev, setSev] = useState("all");
  const refresh = useCallback(async () => setItems((await api.get("/exceptions")).data), []);
  useEffect(() => { refresh(); }, [refresh]);

  const filtered = useMemo(() => sev === "all" ? items : items.filter((i) => i.severity === sev), [items, sev]);
  const counts = useMemo(() => ({
    all: items.length,
    high: items.filter((i) => i.severity === "high").length,
    medium: items.filter((i) => i.severity === "medium").length,
    low: items.filter((i) => i.severity === "low").length,
  }), [items]);

  return (
    <div>
      <PageHeader
        title="Exceptions Queue"
        subtitle="Operational flags"
        actions={
          <button onClick={refresh} data-testid="refresh-exceptions" className="border border-gray-300 hover:bg-gray-900 hover:text-white text-xs font-semibold uppercase tracking-widest px-4 py-2.5">
            Refresh
          </button>
        }
      />
      <div className="p-8">
        {/* filter tabs */}
        <div className="mb-4 flex items-center gap-1 bg-white border border-gray-200 p-1 w-fit">
          {[["all", "All", "text-gray-700"], ["high", "High", "text-red-700"], ["medium", "Medium", "text-amber-700"], ["low", "Low", "text-gray-500"]].map(([key, label, color]) => (
            <button key={key} onClick={() => setSev(key)} data-testid={`sev-filter-${key}`}
              className={`px-3 py-1.5 text-[11px] uppercase tracking-widest font-semibold transition-colors ${sev === key ? "bg-gray-900 text-white" : `${color} hover:bg-gray-100`}`}>
              {label} <span className="font-mono ml-1">{counts[key]}</span>
            </button>
          ))}
        </div>

        <div className="bg-white border border-gray-200" data-testid="exceptions-list">
          {filtered.length === 0 ? (
            <div className="p-12 text-center text-sm text-gray-500">
              {items.length === 0 ? "🎉 No exceptions. Everything looks clean." : "No items match this filter."}
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {filtered.map((x) => {
                const Icon = SEV_ICON[x.severity] || Info;
                const linkFn = ENTITY_LINK[x.entity];
                const href = linkFn ? linkFn(x.entity_id) : null;
                return (
                  <div key={x.id} className="p-4 flex items-start gap-4 hover:bg-gray-50" data-testid={`exception-${x.id}`}>
                    <Icon className={`w-5 h-5 flex-shrink-0 mt-0.5 ${x.severity === "high" ? "text-red-600" : x.severity === "medium" ? "text-amber-600" : "text-gray-500"}`} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className={`text-[10px] uppercase tracking-widest font-bold px-1.5 py-0.5 border ${SEV_STYLE[x.severity]}`}>{x.severity}</span>
                        <span className="font-mono text-xs uppercase tracking-widest font-bold text-gray-900">{x.type.replace(/_/g, " ")}</span>
                        <span className="text-xs text-gray-500 font-mono">· {x.entity}</span>
                      </div>
                      <div className="mt-1 text-sm text-gray-800">
                        {href ? (
                          <Link to={href} className="font-bold hover:text-orange-600" data-testid={`exception-link-${x.id}`}>{x.entity_label}</Link>
                        ) : (
                          <span className="font-bold">{x.entity_label}</span>
                        )}
                        <span className="text-gray-600"> — {x.detail}</span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
